import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { surveyAPI } from '../../services/apiClient';
import BLoading from './ui/BLoading';
import styles from './DeletedSurveysPage.module.css';

const PAGE_SIZE = 20;

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString();
};

const DeletedSurveysPage = () => {
  const [surveys, setSurveys] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, has_next: false, has_prev: false });
  const [isLoading, setIsLoading] = useState(true);
  const [isRestoringId, setIsRestoringId] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [businessIdInput, setBusinessIdInput] = useState('');
  const [businessIdFilter, setBusinessIdFilter] = useState('');

  const fetchDeletedSurveys = useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      const params = {
        page,
        per_page: PAGE_SIZE,
      };

      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (businessIdFilter.trim()) {
        const parsedBusinessId = Number(businessIdFilter);
        if (Number.isFinite(parsedBusinessId) && parsedBusinessId > 0) {
          params.business_id = parsedBusinessId;
        }
      }

      const response = await surveyAPI.getDeletedSurveys(params);
      const data = response.data || {};

      setSurveys(Array.isArray(data.surveys) ? data.surveys : []);
      setPagination(data.pagination || { page: 1, pages: 1, total: 0, has_next: false, has_prev: false });
    } catch (error) {
      console.error('Failed to load deleted surveys:', error);
      toast.error(error.response?.data?.error || 'Failed to load deleted surveys.');
      setSurveys([]);
      setPagination({ page: 1, pages: 1, total: 0, has_next: false, has_prev: false });
    } finally {
      setIsLoading(false);
    }
  }, [businessIdFilter, searchTerm]);

  useEffect(() => {
    fetchDeletedSurveys(1);
  }, [fetchDeletedSurveys]);

  const recoveryStats = useMemo(() => {
    const recoverable = surveys.filter((survey) => survey.is_recoverable).length;
    return {
      recoverable,
      expired: surveys.length - recoverable,
    };
  }, [surveys]);

  const handleApplyFilters = (event) => {
    event.preventDefault();
    setSearchTerm(searchInput);
    setBusinessIdFilter(businessIdInput);
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setBusinessIdInput('');
    setBusinessIdFilter('');
  };

  const handleRestore = async (survey) => {
    const confirmed = window.confirm(
      `Restore \"${survey.title}\"? This will make the survey available in admin management lists again.`
    );
    if (!confirmed) return;

    setIsRestoringId(survey.id);
    try {
      const response = await surveyAPI.restoreDeletedSurvey(survey.id);
      toast.success(response.data?.message || 'Survey restored successfully.');
      await fetchDeletedSurveys(pagination.page || 1);
    } catch (error) {
      console.error('Failed to restore survey:', error);
      toast.error(error.response?.data?.error || 'Failed to restore survey.');
    } finally {
      setIsRestoringId(null);
    }
  };

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={`${styles.title} page-title`}>Deleted Surveys</h1>
          <p className={styles.subtitle}>Review soft-deleted surveys during the retention window and restore when needed.</p>
        </div>
        <div className={styles.countPill}>
          <span>{pagination.total || 0} total</span>
        </div>
      </header>

      <section className={styles.metricsGrid}>
        <article className={styles.metricCard}>
          <p className={styles.metricLabel}>Recoverable</p>
          <p className={styles.metricValue}>{recoveryStats.recoverable}</p>
        </article>
        <article className={styles.metricCard}>
          <p className={styles.metricLabel}>Expired</p>
          <p className={styles.metricValue}>{recoveryStats.expired}</p>
        </article>
      </section>

      <section className={styles.controlsCard}>
        <form className={styles.filters} onSubmit={handleApplyFilters}>
          <div className={styles.filterField}>
            <label htmlFor="deleted-surveys-search">Search</label>
            <input
              id="deleted-surveys-search"
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Survey title or description"
            />
          </div>

          <div className={styles.filterField}>
            <label htmlFor="deleted-surveys-business-id">Business ID</label>
            <input
              id="deleted-surveys-business-id"
              type="number"
              min="1"
              value={businessIdInput}
              onChange={(event) => setBusinessIdInput(event.target.value)}
              placeholder="All businesses"
            />
          </div>

          <div className={styles.filterActions}>
            <button type="submit" className={styles.primaryButton}>Apply</button>
            <button type="button" className={styles.secondaryButton} onClick={handleClearFilters}>Clear</button>
          </div>
        </form>
      </section>

      <section className={styles.tableCard}>
        {isLoading ? (
          <div className={styles.loadingState}><BLoading variant="inline" label="Loading deleted surveys..." /></div>
        ) : surveys.length === 0 ? (
          <div className={styles.emptyState}>No deleted surveys found for the current filters.</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Survey</th>
                  <th>Business</th>
                  <th>Deleted At</th>
                  <th>Purge At</th>
                  <th>Responses</th>
                  <th>Questions</th>
                  <th>Deleted By</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {surveys.map((survey) => (
                  <tr key={survey.id}>
                    <td>
                      <div className={styles.primaryCell}>{survey.title || `Survey ${survey.id}`}</div>
                      <div className={styles.secondaryCell}>ID: {survey.id}</div>
                    </td>
                    <td>
                      <div className={styles.primaryCell}>{survey.business_name || '-'}</div>
                      <div className={styles.secondaryCell}>{survey.business_id ? `ID: ${survey.business_id}` : '-'}</div>
                    </td>
                    <td>{formatDateTime(survey.deleted_at)}</td>
                    <td>{formatDateTime(survey.delete_after)}</td>
                    <td>{survey.response_count ?? 0}</td>
                    <td>{survey.question_count ?? 0}</td>
                    <td>{survey.deleted_by_role || '-'}</td>
                    <td>
                      <span className={survey.is_recoverable ? styles.statusRecoverable : styles.statusExpired}>
                        {survey.is_recoverable ? 'Recoverable' : 'Expired'}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className={styles.actionButton}
                        disabled={!survey.is_recoverable || isRestoringId === survey.id}
                        onClick={() => handleRestore(survey)}
                      >
                        {isRestoringId === survey.id ? 'Restoring...' : 'Restore'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <footer className={styles.pagination}>
        <button
          type="button"
          className={styles.secondaryButton}
          disabled={!pagination.has_prev || isLoading}
          onClick={() => fetchDeletedSurveys((pagination.page || 1) - 1)}
        >
          Previous
        </button>
        <span className={styles.pageMeta}>
          Page {pagination.page || 1} of {pagination.pages || 1}
        </span>
        <button
          type="button"
          className={styles.secondaryButton}
          disabled={!pagination.has_next || isLoading}
          onClick={() => fetchDeletedSurveys((pagination.page || 1) + 1)}
        >
          Next
        </button>
      </footer>
    </main>
  );
};

export default DeletedSurveysPage;
